import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * A camada de dados do reajuste anual (IPCA / IPC-FIPE).
 *
 * Mesmo desenho das outras telas: nenhum componente chama `chamarApi` direto, chave
 * de cache junto de quem a usa, `retry: naoInsistirEmRecusa` em toda consulta
 * (CLAUDE.md, "Regras de API"; `modulos/operacao/condutores/dados.ts` e o
 * precedente).
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. `/reajuste-
 * anual/*` ainda não existe na API (`apps/api/src/modulos/` só tem `eu`, `govbr`
 * e `senha`). Decisão do dono de 2026-08-26 (CLAUDE.md, "Proibido", exceção
 * estendida) para demonstração ao cliente — mesmas três condições dos cinco
 * módulos de cadastro: só leitura, dado claramente fictício, removido antes de
 * `develop` → `hml` (`git grep -n "TEMPORÁRIO" apps/web/src`, checklist em
 * `docs/ALVOS.md`). "Cadastrar" e "Aplicar reajuste" continuam sem gravar nada
 * de verdade — ver `ReajusteAnualPage.tsx`.
 */

/** Um índice de reajuste anual, como a API (ainda inexistente) devolveria. */
export interface ReajusteAnualIndice {
  id: string;
  ano: number;
  /** Percentual do índice, ex.: 4.62 para "4,62%". */
  indice_pct: number | string;
  fonte: string;
  decreto_referencia: string | null;
  vigencia_inicio: string;
  status: 'PENDENTE' | 'APLICADO';
  aplicado_em: string | null;
  tabela_precos_geradas: number | null;
  valores_base_gerados: number | null;
}

/**
 * Uma linha da simulação: o valor de um campo antes e depois do reajuste.
 *
 * `valor_atual`/`valor_novo` seguem a mesma convenção do banco para dinheiro —
 * `numeric(14,2)` entregue como string decimal em reais (CLAUDE.md, "Regras de
 * banco", item 7; `nucleo/dinheiro.ts`) — e não como centavos inteiros. Como a
 * rota ainda não existe, isto é suposição de contrato, registrada aqui para quem
 * escrever a API depois não precisar adivinhar: `formatarDinheiro` já sabe ler os
 * dois formatos (string do banco ou número em reais).
 */
export interface LinhaSimulacao {
  id: string;
  tipo_alvo: string;
  alvo_nome: string;
  campo: string;
  valor_atual: number | string;
  valor_novo: number | string;
}

export const CHAVES_DE_REAJUSTE_ANUAL = {
  indices: ['reajuste-anual', 'indices'] as const,
  simulacao: (ano: number) => ['reajuste-anual', 'simulacao', ano] as const,
};

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Um índice já aplicado (2025) e um pendente (2026), para exercitar os dois
// ramos da coluna "Ações" — "Aplicado" e o botão "Simular".
const REAJUSTES_MOCK: ReajusteAnualIndice[] = [
  {
    id: 'reajuste-exemplo-2026',
    ano: 2026,
    indice_pct: 4.62,
    fonte: 'IPC-FIPE',
    decreto_referencia: 'Decreto Municipal nº 62.000/2026 (exemplo)',
    vigencia_inicio: '2026-01-01',
    status: 'PENDENTE',
    aplicado_em: null,
    tabela_precos_geradas: null,
    valores_base_gerados: null,
  },
  {
    id: 'reajuste-exemplo-2025',
    ano: 2025,
    indice_pct: 3.98,
    fonte: 'IPCA',
    decreto_referencia: 'Decreto Municipal nº 61.500/2025 (exemplo)',
    vigencia_inicio: '2025-01-01',
    status: 'APLICADO',
    aplicado_em: '2025-01-10T09:00:00Z',
    tabela_precos_geradas: 14,
    valores_base_gerados: 22,
  },
];

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Duas linhas de simulação para o índice pendente (2026): uma tabela de preço
// e um valor base, mostrando o valor atual e o proposto lado a lado.
const SIMULACAO_MOCK_POR_ANO: Record<number, LinhaSimulacao[]> = {
  2026: [
    {
      id: 'simulacao-exemplo-01',
      tipo_alvo: 'TABELA_PRECO',
      alvo_nome: 'Bancada A — DRE Exemplo Norte',
      campo: 'valor_km',
      valor_atual: '2.80',
      valor_novo: '2.93',
    },
    {
      id: 'simulacao-exemplo-02',
      tipo_alvo: 'VALOR_BASE',
      alvo_nome: 'Base diária — Convencional',
      campo: 'valor_diario',
      valor_atual: '320.00',
      valor_novo: '334.78',
    },
  ],
};

/** Os índices de reajuste cadastrados, do mais recente para o mais antigo. */
export function useReajustes(): UseQueryResult<ReajusteAnualIndice[], ErroApi> {
  return useQuery<ReajusteAnualIndice[], ErroApi>({
    queryKey: CHAVES_DE_REAJUSTE_ANUAL.indices,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => REAJUSTES_MOCK,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * A simulação de um ano: o que mudaria em cada tabela de preços e valor base se o
 * reajuste fosse aplicado. Não destrutiva — só o "Aplicar" muda algo, e "Aplicar"
 * nem chama API (ver `ReajusteAnualPage.tsx`).
 *
 * `enabled: ano !== null` porque a consulta só faz sentido com o diálogo de
 * simulação aberto para um ano específico.
 */
export function useSimulacaoDeReajuste(
  ano: number | null,
): UseQueryResult<LinhaSimulacao[], ErroApi> {
  return useQuery<LinhaSimulacao[], ErroApi>({
    queryKey: CHAVES_DE_REAJUSTE_ANUAL.simulacao(ano ?? 0),
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => SIMULACAO_MOCK_POR_ANO[ano ?? 0] ?? [],
    enabled: ano !== null,
    retry: naoInsistirEmRecusa,
  });
}
